/**
 * schema.ts — Typed schema for the TUTOR-ONLY game teaching guides.
 *
 * One JSON file per game lives in `./data/gNN.json` (see `index.ts`). The
 * guide text is deliberately NOT part of the public game registry
 * (`public/registry.json`) or the client bundle: it is only served by the
 * role-gated `GET /api/tutor/game-guides[/:gameId]` endpoints.
 *
 * This file contains NO guide content — only the Zod schema and the
 * inferred types — so it is safe to import (types) from client code.
 *
 * All strings are Indonesian prose. They may contain light `**bold**`
 * markup and nothing else; render them through `parseBold()` (bold.ts),
 * never with dangerouslySetInnerHTML.
 */

import { z } from 'zod';

export const TUTOR_GUIDE_GAME_COUNT = 30;
export const TUTOR_GUIDE_LEVEL_COUNT = 5;

const text = z.string().trim().min(1);
const textList = z.array(text).min(1);

/** I = Dikenalkan (introduced), L = Dilatih (practised), U = Diuji (tested). */
export const GuideStageSchema = z.enum(['I', 'L', 'U']);

export const GuideLevelSchema = z
  .object({
    level: z.number().int().min(1).max(TUTOR_GUIDE_LEVEL_COUNT),
    title: text,
    focus: text,
    stage: GuideStageSchema,
  })
  .strict();

export const TutorGuideSchema = z
  .object({
    gameId: z.string().regex(/^g\d{2}$/),
    title: text,
    concept: z
      .object({
        name: text,
        inSimpleWords: text,
        forTutor: text,
      })
      .strict(),
    storyLink: text,
    gameplay: z
      .object({
        whatChildrenDo: text,
        levels: z
          .array(GuideLevelSchema)
          .length(TUTOR_GUIDE_LEVEL_COUNT)
          .refine((levels) => new Set(levels.map((l) => l.level)).size === levels.length, {
            message: 'level numbers must be unique',
          }),
        howDifficultyGrows: text,
      })
      .strict(),
    beforePlaying: textList,
    whilePlaying: textList,
    afterPlaying: textList,
    tips: textList,
    questions: textList,
    unplugged: text,
    commonMistakes: z.array(z.object({ mistake: text, response: text }).strict()).min(1),
    vocabulary: z.array(z.object({ term: text, kidWord: text }).strict()).min(1),
  })
  .strict();

export type GuideStage = z.infer<typeof GuideStageSchema>;
export type GuideLevel = z.infer<typeof GuideLevelSchema>;
export type TutorGuide = z.infer<typeof TutorGuideSchema>;

/** One row of `GET /api/tutor/game-guides`. */
export interface TutorGuideListItem {
  gameId: string;
  /** null while the game has no guide yet. */
  title: string | null;
  conceptName: string | null;
  hasGuide: boolean;
}

export interface TutorGuideListResponse {
  games: TutorGuideListItem[];
  total: number;
  withGuide: number;
}

export interface TutorGuideResponse {
  guide: TutorGuide;
}

/** Stage badge copy shown in the UI. */
export const GUIDE_STAGE_LABEL: Record<GuideStage, string> = {
  I: 'Dikenalkan',
  L: 'Dilatih',
  U: 'Diuji',
};
